import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';

import { updateSettings } from '../../../redux/features/landingCmsSlice';
import ImageField from '../questionbank/ImageField';

/**
 * Site-wide settings: the tag-manager slot, the logo, the WhatsApp button.
 *
 * ---------------------------------------------------------------------------
 * THE GTM FIELD IS THE ONE WITH A HARD RULE
 *
 * The container id is interpolated into an inline <script> on a public,
 * unauthenticated page. Anything that is not a real container id is rejected by
 * the server, and rejected AGAIN by the frontend before it renders — two checks
 * for one value, because the render-time one is what actually protects the page
 * if a value ever reaches the database by some other route.
 *
 * Everything else about analytics stays in GTM itself, which is the point of
 * having a tag manager. There is deliberately no "paste your own HTML" box
 * anywhere in this CMS: a raw-HTML field rendered on a public page is stored
 * XSS with extra steps, and GTM already is the escape hatch.
 */

const GTM_PATTERN = /^GTM-[A-Z0-9]{4,10}$/;

const SettingsPanel = ({ settings, revalidateSeconds }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { saving } = useSelector((state) => state.landingCms);

  const [form, setForm] = useState({
    gtmContainerId: '',
    clarityProjectId: '',
    whatsappNumber: '',
    whatsappMessage: '',
    logoUrl: '',
    logoKey: '',
  });

  useEffect(() => {
    if (!settings) return;
    setForm({
      gtmContainerId: settings.gtmContainerId || '',
      clarityProjectId: settings.clarityProjectId || '',
      whatsappNumber: settings.whatsappNumber || '',
      whatsappMessage: settings.whatsappMessage || '',
      logoUrl: settings.logoUrl || '',
      logoKey: settings.logoKey || '',
    });
  }, [settings]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const gtmValid = !form.gtmContainerId || GTM_PATTERN.test(form.gtmContainerId);

  const save = async () => {
    try {
      await dispatch(updateSettings(form)).unwrap();
      enqueueSnackbar(`Settings saved. Live within ${revalidateSeconds} seconds.`, {
        variant: 'success',
        autoHideDuration: 6000,
      });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Could not save the settings', { variant: 'error' });
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={7}>
        <Stack spacing={2}>
          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Google Tag Manager
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Put the container id here and every tag you configure inside GTM — analytics,
              conversion tracking, remarketing pixels — starts working on the landing page.
              No deploy needed for any of it.
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Container ID"
              placeholder="GTM-XXXXXXX"
              value={form.gtmContainerId}
              onChange={(e) => set({ gtmContainerId: e.target.value.trim().toUpperCase() })}
              error={!gtmValid}
              helperText={
                !gtmValid
                  ? 'Must look like GTM-XXXXXXX. This value goes inside a script tag on a public page, so anything else is rejected.'
                  : form.gtmContainerId
                  ? 'The tag manager will load on the landing page.'
                  : 'Empty means no tag manager is loaded at all.'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="mdi:google-tag-manager" width={18} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              size="small"
              sx={{ mt: 2 }}
              label="Microsoft Clarity project ID"
              value={form.clarityProjectId}
              onChange={(e) => set({ clarityProjectId: e.target.value.trim() })}
              helperText="Session recordings and heatmaps."
            />
          </Card>

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              WhatsApp button
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              The &quot;Connect With Us&quot; button at the foot of the page.
            </Typography>
            <TextField
              fullWidth
              size="small"
              label="Number"
              placeholder="919061000827"
              value={form.whatsappNumber}
              onChange={(e) => set({ whatsappNumber: e.target.value.replace(/\D/g, '') })}
              helperText="Digits only, including the country code. No + and no spaces."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="mdi:whatsapp" width={18} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              sx={{ mt: 2 }}
              label="Prefilled message"
              value={form.whatsappMessage}
              onChange={(e) => set({ whatsappMessage: e.target.value })}
              helperText="What the visitor's message box is pre-populated with."
            />
          </Card>

          <Card sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Logo
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Used in the navigation bar and the footer.
            </Typography>
            <ImageField
              size={72}
              folderName="landing/brand"
              label="Logo URL"
              value={form.logoUrl}
              imageKey={form.logoKey}
              onChange={(url, key) => set({ logoUrl: url, logoKey: key || '' })}
            />
          </Card>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              onClick={save}
              disabled={saving || !gtmValid}
              startIcon={<Icon icon="mdi:content-save" width={18} />}
            >
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Live on the page within {revalidateSeconds}s.
            </Typography>
          </Stack>
        </Stack>
      </Grid>

      <Grid item xs={12} md={5}>
        <Alert severity="info" icon={<Icon icon="mdi:shield-check-outline" />}>
          <AlertTitle>Why there is no &quot;custom HTML&quot; box</AlertTitle>
          <Typography variant="body2">
            A field that injects arbitrary HTML into a public page is a security hole, however
            carefully it is used. Google Tag Manager already is the safe version of that — add
            your scripts as tags inside GTM and they will run here.
          </Typography>
        </Alert>

        <Alert severity="info" sx={{ mt: 2 }} icon={<Icon icon="mdi:clock-outline" />}>
          <AlertTitle>Why changes take a few minutes</AlertTitle>
          <Typography variant="body2">
            The landing page is pre-rendered so its meta tags sit in the HTML the server sends —
            that is the only way Facebook, LinkedIn and WhatsApp can read them, since none of
            them run JavaScript. The page rebuilds itself every {revalidateSeconds} seconds,
            so an edit appears within that window.
          </Typography>
        </Alert>
      </Grid>
    </Grid>
  );
};

export default SettingsPanel;
